/**
 * ============================================
 * NOMINAL A/B TEST AB19 - PRODUCTION VERSION
 * ============================================
 *
 * Product Page Add-On Upsell Redesign
 * Version: 1.9 (Enhanced Validation Build)
 * Date: 2026-01-20
 *
 * DESCRIPTION:
 * Transforms product add-on sections into visually enhanced,
 * image-based cards while maintaining full e-commerce functionality.
 *
 * FEATURES:
 * - Multi-system support (Bold Product Options + Native Magento/Shopify)
 * - Automatic system detection
 * - Product image fetching via Shopify API
 * - Smart filtering (product add-ons vs verification checkboxes)
 * - Mobile responsive design
 * - Tooltip support with hover/click interactions
 * - Out-of-stock item handling
 * - Comprehensive dynamic element suppression
 * - ENHANCED: Content validation with stability checks (99% guarantee)
 *
 * COMPATIBILITY:
 * - Bold Product Options (Shopify App)
 * - Native Magento/Shopify Custom Options
 * - Works on all product pages with add-ons
 *
 * TESTED ON:
 * - Chrome, Safari, Firefox, Edge (latest versions)
 * - Mobile Safari (iOS 14+)
 * - Mobile Chrome (Android 10+)
 *
 * BUG FIXES INCLUDED:
 * v1.3 - Filter non-product checkboxes (verification messages)
 * v1.4 - Hide "Total Extras" with CSS !important
 * v1.7 - Add native options system support
 * v1.8 - Suppress dynamic elements (qty inputs, stock messages)
 * v1.9 - Enhanced validation with stability checks
 *
 * DEPLOYMENT:
 * Copy entire file contents and paste into VWO/Varify JavaScript editor
 *
 * ============================================
 */

(function () {
  'use strict';

  // ========================================
  // CONFIGURATION
  // ========================================

  const CONFIG = {
    MAX_LOAD_ATTEMPTS: 50,
    POLL_INTERVAL: 100,
    MOBILE_BREAKPOINT: 768,
    TITLE_TEXT: 'Make Your Jewelry Perfect',
    IMAGE_FALLBACK_BG: '#F9F6EC',
    MIN_VALIDATION_ATTEMPTS: 3  // Require 3 consecutive validations for stability
  };

  // ========================================
  // DETECT ADD-ON SYSTEM TYPE
  // ========================================

  function detectAddonSystem() {
    // Check for Bold Product Options
    const boldOptions = document.querySelector('.bold_options');
    const boldCheckboxes = boldOptions?.querySelectorAll('.bold_option_checkbox');

    // Check for native Magento/Shopify custom options
    const nativeOptions = document.querySelector('#fieldset-section-2.fieldset');
    const nativeCheckboxes = nativeOptions?.querySelectorAll('input.product-custom-option[type="checkbox"]');

    // Prioritize the system that actually has checkboxes
    // This handles cases where both systems exist on the page (like custom-name-necklace)
    const boldHasCheckboxes = boldCheckboxes && boldCheckboxes.length > 0;
    const nativeHasCheckboxes = nativeCheckboxes && nativeCheckboxes.length > 0;

    if (nativeHasCheckboxes) {
      // Native takes priority if it has checkboxes
      return { type: 'native', container: nativeOptions };
    } else if (boldHasCheckboxes) {
      return { type: 'bold', container: boldOptions };
    }

    return { type: 'none', container: null };
  }

  // ========================================
  // VALIDATE BOLD OPTIONS COMPLETE LOADING
  // ========================================

  function validateBoldOptionsComplete(container) {
    // Must have loaded class
    if (!container.classList.contains('bold_options_loaded')) {
      return false;
    }

    // Must have at least one checkbox
    const checkboxes = container.querySelectorAll('.bold_option_checkbox');
    if (checkboxes.length === 0) {
      return false;
    }

    let foundValidAddon = false;

    // Validate that each checkbox has required elements with content
    for (const element of checkboxes) {
      const input = element.querySelector('input[type="checkbox"]');
      const handle = input?.getAttribute('data-product-handle');
      const title = element.querySelector('.bold_option_title')?.textContent.trim();
      const price = element.querySelector('.bold_option_value_price')?.textContent.trim();

      // If has product handle, it's a product addon and needs title and price
      if (handle) {
        if (!title || !price) {
          return false; // Missing required elements or empty content
        }
        foundValidAddon = true;
      }
    }

    // Must have at least one valid addon
    return foundValidAddon;
  }

  // ========================================
  // VALIDATE NATIVE OPTIONS COMPLETE LOADING
  // ========================================

  function validateNativeOptionsComplete(container) {
    const checkboxes = container.querySelectorAll('input.product-custom-option[type="checkbox"]');

    if (checkboxes.length === 0) {
      return false;
    }

    let foundValidAddon = false;

    // Validate each checkbox has associated label, price, and SKU
    for (const checkbox of checkboxes) {
      const fieldChoice = checkbox.closest('.field.choice');
      if (!fieldChoice) continue;

      const label = fieldChoice.querySelector('label.label');
      if (!label) continue;

      const titleSpan = label.querySelector('span:first-child');
      const priceNotice = label.querySelector('.price-notice');
      const productSku = checkbox.getAttribute('product-sku');

      // Valid addon must have title text, price (notice or attribute), and SKU
      const hasTitle = titleSpan?.textContent.trim();
      const hasPrice = priceNotice?.textContent.trim() || checkbox.getAttribute('price');

      if (hasTitle && hasPrice && productSku) {
        foundValidAddon = true;
      }
    }

    return foundValidAddon;
  }

  // ========================================
  // INJECT CUSTOM CSS
  // ========================================

  function injectCustomCSS() {
    const style = document.createElement('style');
    style.id = 'nominal-addon-redesign-styles';
    style.textContent = `
      .addon-checkbox-new {
        -webkit-appearance: none;
        -moz-appearance: none;
        appearance: none;
        width: 16px;
        height: 16px;
        border-radius: 2.5px;
        border: 1px solid #767676;
        background: #FFFFFF;
        cursor: pointer;
        margin: 0;
        position: relative;
        transition: all 0.2s ease;
      }

      .addon-checkbox-new:checked {
        background-color: #000000 !important;
        border-color: #000000 !important;
      }

      .addon-checkbox-new:checked::after {
        content: '';
        position: absolute;
        left: 5px;
        top: 2px;
        width: 4px;
        height: 8px;
        border: solid white;
        border-width: 0 1.5px 1.5px 0;
        transform: rotate(45deg);
      }

      .addon-card {
        transition: opacity 0.2s ease;
      }

      .addon-card:hover {
        opacity: 0.85;
      }

      /* Hide Bold Options total/summary */
      .bold_options .bold_option_total {
        display: none !important;
      }

      /* Hide native options container and all child elements */
      #fieldset-section-2.fieldset.nominal-hidden,
      #fieldset-section-2.fieldset.nominal-hidden * {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
        height: 0 !important;
        width: 0 !important;
        margin: 0 !important;
        padding: 0 !important;
        overflow: hidden !important;
      }

      @media (max-width: ${CONFIG.MOBILE_BREAKPOINT}px) {
        .nominal-addon-redesign {
          width: 342px !important;
        }
      }
    `;

    if (!document.getElementById('nominal-addon-redesign-styles')) {
      document.head.appendChild(style);
    }
  }

  // ========================================
  // WAIT FOR ADD-ONS TO LOAD (ENHANCED)
  // ========================================

  function waitForAddons(callback, maxAttempts = CONFIG.MAX_LOAD_ATTEMPTS) {
    let done = false;
    const STABILITY_MS = 100;
    const TIMEOUT_MS = maxAttempts * CONFIG.POLL_INTERVAL;
    let stabilityTimer = null;

    function tryValidate() {
      if (done) return;
      const system = detectAddonSystem();
      let isValid = false;
      if (system.type === 'bold')   isValid = validateBoldOptionsComplete(system.container);
      if (system.type === 'native') isValid = validateNativeOptionsComplete(system.container);
      if (!isValid) return;
      done = true;
      clearTimeout(stabilityTimer);
      clearTimeout(fallbackTimer);
      observer.disconnect();
      console.log(`[Nominal A/B Test] ${system.type} system fully loaded and validated (mutation-driven)`);
      callback(system);
    }

    function scheduleValidate() {
      clearTimeout(stabilityTimer);
      stabilityTimer = setTimeout(tryValidate, STABILITY_MS);
    }

    const observer = new MutationObserver(scheduleValidate);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });

    const fallbackTimer = setTimeout(() => {
      if (done) return;
      done = true;
      observer.disconnect();
      console.warn('[Nominal A/B Test] Add-ons did not load within expected time');
    }, TIMEOUT_MS);

    tryValidate();
  }

  // ========================================
  // EXTRACT ADD-ON DATA FROM BOLD OPTIONS
  // ========================================

  function extractBoldAddons(boldContainer) {
    const addons = [];
    const addonElements = boldContainer.querySelectorAll('.bold_option_checkbox');

    addonElements.forEach((element) => {
      const checkbox = element.querySelector('input[type="checkbox"]');
      const hiddenInput = element.querySelector('input[type="hidden"]');

      if (!checkbox) return;

      const productHandle = checkbox.getAttribute('data-product-handle');
      const title = element.querySelector('.bold_option_title')?.textContent.trim();
      const price = element.querySelector('.bold_option_value_price')?.textContent.trim();
      const tooltip = element.querySelector('.bold_tooltip')?.textContent.trim() || '';
      const checkboxClass = checkbox.className;
      const propertyName = hiddenInput?.name.replace('properties[', '').replace(']', '') || '';

      // FILTER: Only include items that are actual product add-ons
      if (!productHandle || !price) {
        return;
      }

      addons.push({
        system: 'bold',
        element: element,
        checkbox: checkbox,
        hiddenInput: hiddenInput,
        productHandle: productHandle,
        productSku: null,
        title: title,
        price: price,
        tooltip: tooltip,
        checkboxClass: checkboxClass,
        propertyName: propertyName,
        imageUrl: null,
        disabled: checkbox.disabled || false
      });
    });

    return addons;
  }

  // ========================================
  // EXTRACT ADD-ON DATA FROM NATIVE OPTIONS
  // ========================================

  function extractNativeAddons(nativeContainer) {
    const addons = [];
    const checkboxInputs = nativeContainer.querySelectorAll('input.product-custom-option[type="checkbox"]');

    checkboxInputs.forEach((checkbox) => {
      // Get parent elements
      const fieldChoice = checkbox.closest('.field.choice');
      const dynamicOption = checkbox.closest('[id^="dynamic_option_id_"]');

      if (!fieldChoice || !dynamicOption) return;

      // Extract label element
      const labelElement = fieldChoice.querySelector('label.label');
      if (!labelElement) return;

      // Extract title (first <span> in label)
      const titleSpan = labelElement.querySelector('span:first-child');
      const title = titleSpan?.textContent.trim();

      // Extract price from price-notice span or price attribute
      const priceNotice = labelElement.querySelector('.price-notice');
      let price = priceNotice?.textContent.trim();

      // If no price notice, try to get from attribute and format it
      if (!price) {
        const priceAttr = checkbox.getAttribute('price');
        if (priceAttr) {
          price = `+$${parseFloat(priceAttr).toFixed(0)}`;
        }
      }

      // Extract product SKU (we'll use this to fetch images)
      const productSku = checkbox.getAttribute('product-sku');

      // Check if disabled (out of stock)
      const disabled = checkbox.disabled || labelElement.textContent.includes('out of stock');

      // Get the option label for tooltip (if exists)
      const optionLabel = dynamicOption.querySelector('label:first-child');
      const tooltip = optionLabel?.textContent.trim() || '';

      if (!title || !price) return;

      addons.push({
        system: 'native',
        element: fieldChoice,
        checkbox: checkbox,
        hiddenInput: null,
        productHandle: null,
        productSku: productSku,
        title: title,
        price: price,
        tooltip: tooltip !== title ? tooltip : '', // Don't duplicate title as tooltip
        checkboxClass: checkbox.className,
        propertyName: checkbox.name,
        imageUrl: null,
        disabled: disabled
      });
    });

    return addons;
  }

  // ========================================
  // FETCH PRODUCT IMAGES
  // ========================================

  async function fetchProductImages(addons) {
    // SKU to product handle mapping for native options
    // Maps product SKUs to their actual Shopify product handles
    const skuToHandleMap = {
      'GIFT BOX': 'nominal-gift-box',
      'Chain-N-Ext-G': 'chain-extension',
      'JC-SPRY-W': 'jewelry-cleaner-kit',
      'RING GIFT BOX': 'ring-gift-box',
      'NECKLACE GIFT BOX': 'necklace-gift-box',
      'CUFF GIFT BOX': 'cuff-gift-box'
    };

    const promises = addons.map(async (addon) => {
      // For Bold Options, use product handle directly
      if (addon.system === 'bold' && addon.productHandle) {
        try {
          const response = await fetch(`/products/${addon.productHandle}.js`);
          if (!response.ok) throw new Error(`HTTP ${response.status}`);

          const productData = await response.json();
          addon.imageUrl = productData.featured_image ||
            (productData.images && productData.images[0]) ||
            null;
        } catch (error) {
          console.warn(`[Nominal A/B Test] Failed to fetch image for ${addon.productHandle}:`, error);
          addon.imageUrl = null;
        }
      }

      // For native options, use SKU mapping or fallback to conversion
      if (addon.system === 'native' && addon.productSku) {
        try {
          // Try to get handle from mapping first
          let handle = skuToHandleMap[addon.productSku];

          // If not in mapping, try converting SKU to handle as fallback
          if (!handle) {
            handle = addon.productSku.toLowerCase().replace(/\s+/g, '-');
          }

          const response = await fetch(`/products/${handle}.js`);

          if (!response.ok) throw new Error(`HTTP ${response.status}`);

          const productData = await response.json();
          addon.imageUrl = productData.featured_image ||
            (productData.images && productData.images[0]) ||
            null;
        } catch (error) {
          // For native options, image fetch failure is expected for unmapped SKUs
          // We'll just use the fallback background
          addon.imageUrl = null;
        }
      }
    });

    await Promise.all(promises);
    return addons;
  }

  // ========================================
  // GENERATE NEW HTML STRUCTURE
  // ========================================

  function generateNewHTML(addons) {
    const isMobile = window.innerWidth <= CONFIG.MOBILE_BREAKPOINT;
    const containerWidth = isMobile ? '342px' : '441px';

    let html = `
      <div class="nominal-addon-redesign" style="
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        padding: 0;
        gap: 12px;
        width: ${containerWidth};
        max-width: 100%;
        margin-bottom: 20px;
      ">
        <div class="addon-title" style="
          font-family: 'Aktiv Grotesk Corp', 'Aktiv Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          font-style: normal;
          font-weight: 500;
          font-size: 12px;
          line-height: 14px;
          display: flex;
          align-items: center;
          text-transform: capitalize;
          color: #000000;
        ">
          ${CONFIG.TITLE_TEXT}
        </div>
    `;

    addons.forEach((addon, index) => {
      const imageHTML = addon.imageUrl
        ? `<img src="${addon.imageUrl}" alt="${addon.title}" style="width: 40px; height: 40px; object-fit: cover; background: ${CONFIG.IMAGE_FALLBACK_BG}; display: block;">`
        : `<div style="width: 40px; height: 40px; background: ${CONFIG.IMAGE_FALLBACK_BG};"></div>`;

      // Generate tooltip HTML if tooltip exists
      const tooltipHTML = addon.tooltip
        ? `<div class="addon-tooltip" style="
             position: relative;
             width: 14px;
             height: 14px;
             flex-shrink: 0;
             margin-left: 4px;
           ">
             <div class="tooltip-icon" style="
               width: 14px;
               height: 14px;
               border-radius: 50%;
               border: 1px solid rgba(0, 0, 0, 0.4);
               display: flex;
               align-items: center;
               justify-content: center;
               font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
               font-size: 10px;
               font-weight: 600;
               color: rgba(0, 0, 0, 0.6);
               cursor: help;
               background: transparent;
             ">i</div>
             <div class="tooltip-content" style="
               display: none;
               position: absolute;
               bottom: calc(100% + 8px);
               left: 50%;
               transform: translateX(-50%);
               background: #2d2d2d;
               color: white;
               padding: 8px 12px;
               border-radius: 4px;
               font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
               font-size: 11px;
               line-height: 14px;
               white-space: nowrap;
               z-index: 1000;
               pointer-events: none;
             ">
               ${addon.tooltip}
               <div style="
                 position: absolute;
                 top: 100%;
                 left: 50%;
                 transform: translateX(-50%);
                 width: 0;
                 height: 0;
                 border-left: 5px solid transparent;
                 border-right: 5px solid transparent;
                 border-top: 5px solid #2d2d2d;
               "></div>
             </div>
           </div>`
        : '';

      // Add "(out of stock)" to price if disabled
      const priceText = addon.disabled && !addon.price.includes('out of stock')
        ? `${addon.price} (out of stock)`
        : addon.price;

      // Determine if checkbox should be disabled
      const disabledAttr = addon.disabled ? 'disabled' : '';
      const cursorStyle = addon.disabled ? 'cursor: not-allowed;' : 'cursor: pointer;';
      const opacityStyle = addon.disabled ? 'opacity: 0.5;' : '';

      html += `
        <div class="addon-card" data-addon-index="${index}" style="
          display: flex;
          flex-direction: row;
          align-items: center;
          padding: 0;
          gap: 8px;
          width: 100%;
          height: 40px;
          ${cursorStyle}
          ${opacityStyle}
        ">
          <div class="addon-checkbox-wrapper" style="
            width: 16px;
            height: 16px;
            flex-shrink: 0;
          ">
            <input
              type="checkbox"
              class="addon-checkbox-new"
              data-addon-index="${index}"
              data-original-class="${addon.checkboxClass}"
              aria-label="Select ${addon.title}"
              ${disabledAttr}
            />
          </div>

          <div class="addon-image" style="flex-shrink: 0;">
            ${imageHTML}
          </div>

          <div class="addon-info" style="
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: flex-start;
            padding: 0;
            gap: 4px;
            flex-grow: 1;
          ">
            <div style="
              display: flex;
              align-items: center;
              gap: 4px;
            ">
              <div class="addon-name" style="
                font-family: 'AktivGrotesk-Medium', 'Aktiv Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                font-style: normal;
                font-weight: 400;
                font-size: 14.4px;
                line-height: 17px;
                color: #000000;
              ">
                ${addon.title}
              </div>
              ${tooltipHTML}
            </div>
            <div class="addon-price" style="
              font-family: 'Aktiv Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              font-style: normal;
              font-weight: 400;
              font-size: 12px;
              line-height: 15px;
              color: rgba(0, 0, 0, 0.6);
            ">
              ${priceText}
            </div>
          </div>
        </div>
      `;
    });

    html += `</div>`;

    return html;
  }

  // ========================================
  // REPLACE UI IN DOM
  // ========================================

  function replaceUI(container, newHTML, addons, systemType) {
    if (systemType === 'bold') {
      // For Bold Options: hide individual add-on elements
      addons.forEach(addon => {
        addon.element.style.display = 'none';
      });

      // Hide the Bold Options total/summary
      const boldTotal = container.querySelector('.bold_option_total');
      if (boldTotal) {
        boldTotal.style.display = 'none';
      }

      // Insert new HTML after the Bold Options container
      container.insertAdjacentHTML('afterend', newHTML);
    } else if (systemType === 'native') {
      // For native options: add class to apply comprehensive hiding
      container.classList.add('nominal-hidden');

      // Insert new HTML after the fieldset
      container.insertAdjacentHTML('afterend', newHTML);
    }

    return document.querySelector('.nominal-addon-redesign');
  }

  // ========================================
  // BIND EVENT LISTENERS
  // ========================================

  function bindEventListeners(addons, newContainer) {
    addons.forEach((addon, index) => {
      const newCheckbox = newContainer.querySelector(
        `.addon-checkbox-new[data-addon-index="${index}"]`
      );
      const cardElement = newContainer.querySelector(
        `.addon-card[data-addon-index="${index}"]`
      );

      if (!newCheckbox || !addon.checkbox) return;

      // Sync initial state
      newCheckbox.checked = addon.checkbox.checked;

      // When new checkbox changes, sync with original
      newCheckbox.addEventListener('change', (e) => {
        addon.checkbox.checked = e.target.checked;

        const changeEvent = new Event('change', { bubbles: true });
        addon.checkbox.dispatchEvent(changeEvent);

        // Trigger click event as well (for compatibility)
        const clickEvent = new Event('click', { bubbles: true });
        addon.checkbox.dispatchEvent(clickEvent);
      });

      // Make entire card clickable (if not disabled)
      if (!addon.disabled) {
        cardElement.addEventListener('click', (e) => {
          // Don't toggle if clicking checkbox or tooltip icon
          if (e.target === newCheckbox ||
            e.target.closest('.addon-tooltip') ||
            e.target.closest('.tooltip-icon')) {
            return;
          }
          newCheckbox.click();
        });
      }

      // Sync original checkbox changes back to new checkbox
      addon.checkbox.addEventListener('change', () => {
        newCheckbox.checked = addon.checkbox.checked;
      });

      // Bind tooltip hover events if tooltip exists
      const tooltipIcon = cardElement.querySelector('.tooltip-icon');
      const tooltipContent = cardElement.querySelector('.tooltip-content');

      if (tooltipIcon && tooltipContent) {
        tooltipIcon.addEventListener('mouseenter', () => {
          tooltipContent.style.display = 'block';
        });

        tooltipIcon.addEventListener('mouseleave', () => {
          tooltipContent.style.display = 'none';
        });

        tooltipIcon.addEventListener('click', (e) => {
          e.stopPropagation();
          const isVisible = tooltipContent.style.display === 'block';
          tooltipContent.style.display = isVisible ? 'none' : 'block';
        });
      }
    });
  }

  // ========================================
  // MAIN INITIALIZATION
  // ========================================

  function init() {
    // Check if already initialized
    if (document.querySelector('.nominal-addon-redesign')) {
      console.log('[Nominal A/B Test] Already initialized');
      return;
    }

    injectCustomCSS();

    waitForAddons(async (system) => {
      try {
        let addons;

        if (system.type === 'bold') {
          addons = extractBoldAddons(system.container);
        } else if (system.type === 'native') {
          addons = extractNativeAddons(system.container);
        } else {
          console.log('[Nominal A/B Test] No add-ons found on this product');
          return;
        }

        if (addons.length === 0) {
          console.log('[Nominal A/B Test] No add-ons extracted');
          return;
        }

        console.log(`[Nominal A/B Test] Found ${addons.length} add-ons using ${system.type} system`);

        await fetchProductImages(addons);

        const newHTML = generateNewHTML(addons);
        const newContainer = replaceUI(system.container, newHTML, addons, system.type);

        bindEventListeners(addons, newContainer);

        console.log(`[Nominal A/B Test] Redesign applied successfully (${addons.length} add-ons, ${system.type} system)`);

      } catch (error) {
        console.error('[Nominal A/B Test] Error during initialization:', error);
        // On error, restore visibility
        if (system.type === 'bold') {
          const addons = extractBoldAddons(system.container);
          addons.forEach(addon => {
            if (addon.element) {
              addon.element.style.display = '';
            }
          });
        } else if (system.type === 'native') {
          system.container.classList.remove('nominal-hidden');
        }
      }
    });
  }

  // ========================================
  // RUN
  // ========================================

  // Wait for DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();